import { Link } from "react-router-dom";
import { PageLayout } from "@/components/PageLayout";
import { CalculatorCard } from "@/components/CalculatorCard";
import { Beaker, Droplet, TestTube, FlaskConical, Atom, Calculator, Scale, Percent, Wind, TrendingUp, Battery, Orbit, Clock, Flame, Search } from "lucide-react";
import { useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import logoLight from "@/assets/logo-light_t.png";
import logoDark from "@/assets/logo-dark_t.png";
import { useTranslation } from "react-i18next";
import { useLanguageAwareSEO } from "@/hooks/useLanguageAwareSEO";
import { breadcrumbSchema } from "@/utils/structuredData";

const simpleCalculators = {
  solutions: [
    {
      title: "Molarity Calculator",
      description: "Moles per liter",
      icon: Beaker,
      href: "/calculator/molarity",
      category: "chemistry" as const,
    },
    {
      title: "Dilution Calculator",
      description: "C₁V₁ = C₂V₂",
      icon: Droplet,
      href: "/calculator/dilution",
      category: "chemistry" as const,
    },
    {
      title: "Mass to Moles",
      description: "Convert mass to moles",
      icon: Scale,
      href: "/calculator/mass-to-moles",
      category: "chemistry" as const,
    },
    {
      title: "Moles to Mass",
      description: "Convert moles to mass",
      icon: Scale,
      href: "/calculator/moles-to-mass",
      category: "chemistry" as const,
    },
    {
      title: "Percent Composition",
      description: "Element percentage by mass",
      icon: Percent,
      href: "/calculator/percent-composition",
      category: "chemistry" as const,
    },
  ],
  stoichiometry: [
    {
      title: "Stoichiometry",
      description: "Reactant and product ratios",
      icon: Atom,
      href: "/calculator/stoichiometry",
      category: "chemistry" as const,
    },
    {
      title: "Limiting Reactant",
      description: "Find limiting reactant",
      icon: FlaskConical,
      href: "/calculator/limiting-reactant",
      category: "chemistry" as const,
    },
    {
      title: "Percent Yield",
      description: "Actual vs theoretical yield",
      icon: Percent,
      href: "/calculator/percent-yield",
      category: "chemistry" as const,
    },
    {
      title: "Empirical Formula",
      description: "Simplest formula from composition",
      icon: Calculator,
      href: "/calculator/empirical-formula",
      category: "chemistry" as const,
    },
    {
      title: "Molecular Formula",
      description: "Actual formula from empirical",
      icon: Atom,
      href: "/calculator/molecular-formula",
      category: "chemistry" as const,
    },
  ],
  acidsAndBases: [
    {
      title: "pH Calculator",
      description: "Strong/weak acids and bases",
      icon: TestTube,
      href: "/calculator/ph",
      category: "chemistry" as const,
    },
    {
      title: "pOH Calculator",
      description: "Calculate pOH values",
      icon: TestTube,
      href: "/calculator/poh",
      category: "chemistry" as const,
    },
    {
      title: "H⁺ Concentration",
      description: "Hydrogen ion concentration",
      icon: Atom,
      href: "/calculator/hydrogen-ion",
      category: "chemistry" as const,
    },
    {
      title: "Buffer Solution",
      description: "Henderson-Hasselbalch equation",
      icon: FlaskConical,
      href: "/calculator/buffer-solution",
      category: "chemistry" as const,
    },
  ],
  gasLaws: [
    {
      title: "Ideal Gas Law",
      description: "PV = nRT",
      icon: Wind,
      href: "/calculator/ideal-gas-law",
      category: "chemistry" as const,
    },
    {
      title: "Boyle's Law",
      description: "P₁V₁ = P₂V₂",
      icon: Wind,
      href: "/calculator/boyles-law",
      category: "chemistry" as const,
    },
    {
      title: "Charles's Law",
      description: "V₁/T₁ = V₂/T₂",
      icon: Wind,
      href: "/calculator/charles-law",
      category: "chemistry" as const,
    },
    {
      title: "Combined Gas Law",
      description: "Combined P, V, T relationships",
      icon: Wind,
      href: "/calculator/combined-gas-law",
      category: "chemistry" as const,
    },
  ],
};

const advancedCalculators = {
  equilibrium: [
    {
      title: "Equilibrium Constant",
      description: "Kc/Kp calculations",
      icon: TrendingUp,
      href: "/calculator/equilibrium-constant",
      category: "chemistry" as const,
    },
    {
      title: "Reaction Quotient",
      description: "Q calculations",
      icon: Calculator,
      href: "/calculator/reaction-quotient",
      category: "chemistry" as const,
    },
    {
      title: "ICE Table Solver",
      description: "Initial, Change, Equilibrium",
      icon: TestTube,
      href: "/calculator/ice-table",
      category: "chemistry" as const,
    },
  ],
  thermochemistry: [
    {
      title: "Enthalpy Change",
      description: "ΔH calculations",
      icon: Flame,
      href: "/calculator/enthalpy-change",
      category: "chemistry" as const,
    },
    {
      title: "Hess's Law",
      description: "Calculate ΔH from steps",
      icon: Calculator,
      href: "/calculator/hess-law",
      category: "chemistry" as const,
    },
    {
      title: "Gibbs Free Energy",
      description: "ΔG = ΔH - TΔS",
      icon: TrendingUp,
      href: "/calculator/gibbs-free-energy",
      category: "chemistry" as const,
    },
  ],
  electrochemistry: [
    {
      title: "Nernst Equation",
      description: "Cell potential calculations",
      icon: Battery,
      href: "/calculator/nernst-equation",
      category: "chemistry" as const,
    },
    {
      title: "Cell Potential",
      description: "Standard electrode potential",
      icon: Battery,
      href: "/calculator/cell-potential",
      category: "chemistry" as const,
    },
  ],
  atomicStructure: [
    {
      title: "Electron Configuration",
      description: "Electron arrangement tool",
      icon: Orbit,
      href: "/calculator/electron-configuration",
      category: "chemistry" as const,
    },
    {
      title: "Orbital Diagram",
      description: "Visualize electron orbitals",
      icon: Orbit,
      href: "/calculator/orbital-diagram",
      category: "chemistry" as const,
    },
  ],
  kinetics: [
    {
      title: "Rate Law",
      description: "Reaction rate calculations",
      icon: Clock,
      href: "/calculator/rate-law",
      category: "chemistry" as const,
    },
    {
      title: "Half-Life",
      description: "Calculate half-life",
      icon: Clock,
      href: "/calculator/half-life",
      category: "chemistry" as const,
    },
    {
      title: "Activation Energy",
      description: "Arrhenius equation",
      icon: Flame,
      href: "/calculator/activation-energy",
      category: "chemistry" as const,
    },
  ],
};

export default function Chemistry() {
  const { t } = useTranslation();
  const [level, setLevel] = useState<"simple" | "advanced">("simple");

  useLanguageAwareSEO([
    (lang) => breadcrumbSchema([
      { name: "Home", url: "https://calclab.app" },
      { name: t('categories.pages.chemistry.title'), url: "https://calclab.app/chemistry" }
    ], lang)
  ]);

  return (
    <PageLayout>
      <div className="max-w-7xl mx-auto">
        <div className="mb-8 text-center">
          <h1 className="text-3xl md:text-4xl font-bold mb-2">{t('categories.pages.chemistry.title')}</h1>
          <p className="text-muted-foreground">
            {t('categories.pages.chemistry.description')}
          </p>
        </div>

        <div className="flex justify-center mb-8">
          <Tabs value={level} onValueChange={(v) => setLevel(v as "simple" | "advanced")} className="w-full max-w-md">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="simple">{t('categories.pages.chemistry.simple')}</TabsTrigger>
              <TabsTrigger value="advanced">{t('categories.pages.chemistry.advanced')}</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {level === "simple" ? (
          <Tabs defaultValue="solutions" className="w-full">
            <TabsList className="grid w-full grid-cols-2 lg:grid-cols-4 mb-8">
              <TabsTrigger value="solutions">Solutions</TabsTrigger>
              <TabsTrigger value="stoichiometry">Stoichiometry</TabsTrigger>
              <TabsTrigger value="acidsAndBases">Acids & Bases</TabsTrigger>
              <TabsTrigger value="gasLaws">Gas Laws</TabsTrigger>
            </TabsList>

            <TabsContent value="solutions">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {simpleCalculators.solutions.map((calc) => (
                  <CalculatorCard key={calc.title} {...calc} />
                ))}
              </div>
            </TabsContent>

            <TabsContent value="stoichiometry">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {simpleCalculators.stoichiometry.map((calc) => (
                  <CalculatorCard key={calc.title} {...calc} />
                ))}
              </div>
            </TabsContent>

            <TabsContent value="acidsAndBases">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {simpleCalculators.acidsAndBases.map((calc) => (
                  <CalculatorCard key={calc.title} {...calc} />
                ))}
              </div>
            </TabsContent>

            <TabsContent value="gasLaws">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {simpleCalculators.gasLaws.map((calc) => (
                  <CalculatorCard key={calc.title} {...calc} />
                ))}
              </div>
            </TabsContent>
          </Tabs>
        ) : (
          <Tabs defaultValue="equilibrium" className="w-full">
            <TabsList className="grid w-full grid-cols-2 lg:grid-cols-5 mb-8">
              <TabsTrigger value="equilibrium">Equilibrium</TabsTrigger>
              <TabsTrigger value="thermochemistry">Thermochemistry</TabsTrigger>
              <TabsTrigger value="electrochemistry">Electrochemistry</TabsTrigger>
              <TabsTrigger value="atomicStructure">Atomic Structure</TabsTrigger>
              <TabsTrigger value="kinetics">Kinetics</TabsTrigger>
            </TabsList>

            <TabsContent value="equilibrium">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {advancedCalculators.equilibrium.map((calc) => (
                  <CalculatorCard key={calc.title} {...calc} />
                ))}
              </div>
            </TabsContent>

            <TabsContent value="thermochemistry">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {advancedCalculators.thermochemistry.map((calc) => (
                  <CalculatorCard key={calc.title} {...calc} />
                ))}
              </div>
            </TabsContent>

            <TabsContent value="electrochemistry">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {advancedCalculators.electrochemistry.map((calc) => (
                  <CalculatorCard key={calc.title} {...calc} />
                ))}
              </div>
            </TabsContent>

            <TabsContent value="atomicStructure">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {advancedCalculators.atomicStructure.map((calc) => (
                  <CalculatorCard key={calc.title} {...calc} />
                ))}
              </div>
            </TabsContent>

            <TabsContent value="kinetics">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {advancedCalculators.kinetics.map((calc) => (
                  <CalculatorCard key={calc.title} {...calc} />
                ))}
              </div>
            </TabsContent>
          </Tabs>
        )}
      </div>
    </PageLayout>
  );
}
